import { referentiel as r } from '@admin-pmvb/core'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  corps,
  ecrit,
  type Entite,
  lire,
  requeteMoi,
  requetes,
  type Tiers as TypeTiers,
} from '../api'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { LogoDe } from '../composants/Logo'
import { Ligne, Section, Vide } from '../composants/Section'
import { LIBELLES_ROLE_TIERS, options } from '../libelles'
import { EntetePage } from '../composants/Ui'
import { OngletsReferentiel } from '../composants/OngletsReferentiel'

const champsTiers = (entites: Entite[], administrateur: boolean): Champ[] => [
  { nom: 'nom', libelle: 'Nom', type: 'texte', requis: true },
  {
    nom: 'role',
    libelle: 'Rôle',
    type: 'choix',
    requis: true,
    options: options(LIBELLES_ROLE_TIERS),
  },
  {
    nom: 'entiteParDefautId',
    libelle: 'Entité par défaut',
    type: 'choix',
    requis: !administrateur,
    aide: administrateur
      ? 'Sans entité, le tiers est partagé et visible des seuls administrateurs.'
      : undefined,
    options: entites.filter((e) => !e.archivee).map((e) => ({ valeur: e.id, libelle: e.nom })),
  },
  { nom: 'siren', libelle: 'SIREN', type: 'texte' },
  { nom: 'iban', libelle: 'IBAN', type: 'texte' },
  {
    nom: 'libellesBancaires',
    libelle: 'Libellés bancaires',
    type: 'liste',
    aide: 'Un par ligne : textes qui l’identifient sur les relevés.',
  },
  {
    nom: 'adressesMail',
    libelle: 'Adresses mail',
    type: 'liste',
    aide: 'Une par ligne : adresse (sylvie@exemple.fr) ou domaine (exemple.fr). Sa réponse à une tâche qui l’attend est reconnue.',
  },
]

export function Tiers() {
  const moi = useSuspenseQuery(requeteMoi).data
  const client = useQueryClient()
  const tiers = useQuery(requetes.tiers)
  const entites = useQuery(requetes.entites)
  const [filtre, setFiltre] = useState('')
  const listeEntites = entites.data ?? []
  const nomEntite = (id: string | null) => listeEntites.find((e) => e.id === id)?.nom
  const rafraichir = () => client.invalidateQueries()
  // Entités où l'on écrit : pas un espace privé lu par un accès temporaire (lecture seule).
  const champs = champsTiers(
    listeEntites.filter((e) => ecrit(moi, e.id)),
    moi.administrateur,
  )
  const modifiable = (t: TypeTiers) =>
    t.entiteParDefautId === null ? moi.administrateur : ecrit(moi, t.entiteParDefautId)
  const affiches = (tiers.data ?? []).filter((t) => !filtre || t.role === filtre)

  return (
    <main>
      <EntetePage surtitre="Référentiel" titre="Tiers" />
      <OngletsReferentiel actif="tiers" />
      <Section
        titre="Cabinets, fournisseurs, clients, locataires…"
        libelleAjout="Nouveau tiers"
        ajout={(fermer) => (
          <Formulaire
            champs={champs}
            onAnnuler={fermer}
            onValider={async (v) => {
              await lire(api.tiers.$post({ json: corps(r.tiersCreation, v) }))
              await rafraichir()
              fermer()
            }}
          />
        )}
      >
        <label className="filtre">
          Rôle{' '}
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)}>
            <option value="">Tous</option>
            {options(LIBELLES_ROLE_TIERS).map((o) => (
              <option key={o.valeur} value={o.valeur}>
                {o.libelle}
              </option>
            ))}
          </select>
        </label>
        {tiers.isPending && <Vide>Chargement…</Vide>}
        {tiers.data && affiches.length === 0 && <Vide>Aucun tiers.</Vide>}
        {affiches.map((t) => (
          <Ligne
            key={t.id}
            edition={
              modifiable(t)
                ? (fermer) => (
                    <Formulaire
                      champs={champs}
                      initial={t}
                      onAnnuler={fermer}
                      onValider={async (v) => {
                        await lire(
                          api.tiers[':id'].$patch({
                            param: { id: t.id },
                            json: corps(r.tiersModification, v),
                          }),
                        )
                        await rafraichir()
                        fermer()
                      }}
                    />
                  )
                : undefined
            }
          >
            <span className="titre-logo">
              <LogoDe nom={t.nom} />
              <Link to="/tiers/$id" params={{ id: t.id }}>
                <strong>{t.nom}</strong>
              </Link>
            </span>
            <span className="discret">
              {' '}
              {[
                LIBELLES_ROLE_TIERS[t.role],
                nomEntite(t.entiteParDefautId) ?? 'partagé',
                t.siren && `SIREN ${t.siren}`,
                t.adressesMail.length > 0 && t.adressesMail.join(', '),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </Ligne>
        ))}
      </Section>
    </main>
  )
}
